import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';

export const PAGE_SIZE = 25;

/* The hub calls the population with no lab material "awaiting_lab" (its
 * count key in /specimens/hub); the overview's `state=` calls it "awaiting"
 * (routes/specimen_overview.py VIEW_STATES). */
const SERVER_STATE = { awaiting_lab: 'awaiting' };

/**
 * The overview's origin= filter: only isolates whose origin is a live field
 * collection ('field') or a live intake ('external'). The Collection and
 * Intake nodes' "awaiting lab" counts open their table with it, so the table
 * lists exactly the isolates the count counted (hub.collection.awaiting_lab,
 * hub.intake.awaiting_lab).
 */
export const ORIGINS = ['field', 'external'];

/** GET /specimens/overview's query from the hub's URL state. No view is the
 *  whole table ("all"); the others filter its rows. */
export function overviewParams({ view, node, state, unit, origin, q, sort, order, page } = {}) {
  const params = { view: view || 'all', limit: PAGE_SIZE, offset: Math.max(0, Number(page) || 0) * PAGE_SIZE };
  if (node) params.node = node;
  if (state) params.state = SERVER_STATE[state] || state;
  if (unit) params.unit = unit;
  if (ORIGINS.includes(origin)) params.origin = origin;
  if (q && q.trim()) params.q = q.trim();
  if (sort) params.sort = sort;
  if (order) params.order = order;
  return params;
}

/**
 * One page of the hub's table (GET /specimens/overview), and a way to re-read
 * just the rows an action touched (`ids=`) without losing the reader's place.
 *
 * An answer that arrives after the query moved on is dropped: typing in the
 * search box sends a request per pause, and the slowest must not win.
 */
export default function useIsolatesOverview(query, { enabled = true } = {}) {
  const [data, setData] = useState({ items: [], total: 0, truncated: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);
  const key = JSON.stringify(overviewParams(query));

  const load = useCallback(() => {
    if (!enabled) return Promise.resolve();
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    return api.get('/specimens/overview', { params: JSON.parse(key) })
      .then((res) => {
        if (mine !== seq.current) return;
        const body = res?.data || {};
        const items = Array.isArray(body.items) ? body.items : [];
        setData({ items, total: body.total ?? items.length, truncated: !!body.truncated });
      })
      .catch((e) => { if (mine === seq.current) setError(errorText(e)); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [key, enabled]);

  useEffect(() => { load(); }, [load]);

  /** Re-read these isolates in place. One no longer under this filter (an
   *  action moved it on) stays until the next full load, so a row does not
   *  vanish from under the button just pressed. */
  const refreshRows = useCallback((ids) => {
    const list = (ids || []).filter(Boolean);
    if (!list.length || !enabled) return Promise.resolve();
    const params = { ...JSON.parse(key), ids: list.join(','), offset: 0, limit: Math.min(100, list.length) };
    return api.get('/specimens/overview', { params })
      .then((res) => {
        const fresh = new Map((res?.data?.items || []).map((i) => [i.specimen_id, i]));
        setData((d) => ({ ...d, items: d.items.map((i) => fresh.get(i.specimen_id) || i) }));
      })
      .catch(() => {});
  }, [key, enabled]);

  return { ...data, loading, error, reload: load, refreshRows };
}
